import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { DataLoanDefinitionTct, LoanDefinitionType } from '@dealstack/shared/lib/generated';
import { ActionIcon, Flex } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

type Handlers = {
	onEdit: (loan: DataLoanDefinitionTct) => void;
};

type GetLoansTableColumnsParams = {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	getSecurityTypeTranslation: (type: LoanDefinitionType) => string;
	handlers: Handlers;
};

export const getLoansTableColumns = ({
	tMipManage,
	getSecurityTypeTranslation,
	handlers,
}: GetLoansTableColumnsParams): ExtendedColumnDef<DataLoanDefinitionTct>[] => {
	const { onEdit } = handlers;

	return [
		createTextColumn({
			id: 'type',
			headerText: tMipManage('root.loans.table.header.type'),
			filterText: tMipManage('root.loans.table.filter.type'),
			columnLabel: tMipManage('root.loans.table.header.type'),
			accessorFn: (row) => getSecurityTypeTranslation(row.type),
			enableGlobalFilter: true,
			showFilter: true,
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: Object.values(LoanDefinitionType).map((type) => ({
					value: type,
					label: getSecurityTypeTranslation(type),
				})),
			},
		}),
		createTextColumn({
			id: 'name',
			headerText: tMipManage('root.loans.table.header.name'),
			filterText: tMipManage('root.loans.table.filter.name'),
			columnLabel: tMipManage('root.loans.table.header.name'),
			accessorKey: 'name',
			enableGlobalFilter: true,
			showFilter: true,
			filterVariant: 'text',
			cell: ({ getValue }) => {
				const value = getValue() as string;
				return value || LONG_DASH;
			},
		}),
		createTextColumn({
			id: 'funder',
			headerText: tMipManage('root.loans.table.header.lender'),
			filterText: tMipManage('root.loans.table.filter.lender'),
			columnLabel: tMipManage('root.loans.table.header.lender'),
			accessorKey: 'funder',
			enableGlobalFilter: true,
			showFilter: true,
			filterVariant: 'text',
			cell: ({ getValue }) => {
				const value = getValue() as string;
				return value || LONG_DASH;
			},
		}),
		{
			id: 'actions',
			header: '',
			enableSorting: false,
			enableColumnFilter: false,
			size: 80,
			cell: ({ row }) => (
				<Flex justify="flex-end" w="100%">
					<ActionIcon onClick={() => onEdit(row.original)} size="lg">
						<Icon variant="pencil" size="lg" color="black" />
					</ActionIcon>
				</Flex>
			),
		},
	];
};
